import { createApp, h, type App } from 'vue'


import Modal from '../components/elements/modal/Modal.vue'

interface ModalButton {
  text?: string
  type?: string
  variant?: string
  outline?: boolean
  flat?: boolean
  callback?: () => Promise<boolean> | boolean
  [key: string]: any
}

interface ModalOptions {
  title?: string
  confirm?: ModalButton
  cancel?: ModalButton | boolean
  icon?: string
  variant?: string
  container?: string | object | null
}

export interface ModalPlugin {
  create(message: string, options?: ModalOptions): any
  destroy(modal: any): void
  alert(message: string, options?: ModalOptions): any
  confirm(message: string, options?: ModalOptions): any
}

function destroyModal(container: HTMLElement, app: App) {
  app.unmount()
  container.remove()
}

export default defineNuxtPlugin((nuxtApp) => {
  // Register OModal as a global component
  nuxtApp.vueApp.component('OModal', Modal as any)

  const plugin: ModalPlugin = {
    create(message: string, { title, confirm, cancel, icon, variant, container }: ModalOptions = {}) {
      const mountContainer = document.createElement('div')
      document.body.appendChild(mountContainer)

      // Build the options array from confirm/cancel props
      const options: ModalButton[] = []
      if (confirm) {
        options.push({ type: 'confirm', ...confirm })
      }
      if (cancel) {
        const cancelOption = typeof cancel === 'boolean'
          ? { type: 'cancel' }
          : { type: 'cancel', ...cancel }
        options.push(cancelOption)
      }

      let modalInstance: any = null
      let modalApp: App | null = null

      modalApp = createApp({
        setup() {
          return () => h(Modal, {
            info: message,
            title,
            icon: icon || null,
            options,
            container,
            ref: 'modal',
            onClose: () => {
              if (modalApp && mountContainer) {
                destroyModal(mountContainer, modalApp)
              }
            },
          })
        },
      })

      // Share the parent app's global config so i18n, router, etc. are available
      modalApp.config.globalProperties = nuxtApp.vueApp.config.globalProperties

      // Copy registered global components so Modal can use o-button, o-icon, etc.
      const parentComponents = (nuxtApp.vueApp as any)._context?.components
      if (parentComponents) {
        Object.assign((modalApp as any)._context.components, parentComponents)
      }

      const mounted = modalApp.mount(mountContainer)
      modalInstance = (mounted as any).$refs.modal

      if (modalInstance?.open) {
        return modalInstance.open()
      }

      return Promise.resolve()
    },

    destroy(modal: any) {
      // This is kept for API compatibility but cleanup is now handled
      // automatically via the onClose handler in create()
    },

    alert(message: string, options: ModalOptions = {}) {
      const { title, variant, icon, confirm, container } = options
      return this.create(message, { title, confirm, variant, icon, container })
    },

    confirm(message: string, options: ModalOptions = {}) {
      const { title, variant, icon, confirm, container } = options
      const cancel = Object.assign({ cancel: true }, options.cancel)

      return this.create(message, { title, cancel, confirm, variant, icon, container })
    },
  }

  nuxtApp.provide('modal', plugin)
})
